/**
 * Starring clubs and players.
 *
 * Thin on purpose. The interesting decisions are in `src/data/favourites.js`
 * (what a favourite is) and in the Firestore rules (what may be stored).
 * This layer adds the two things neither can do: the count cap, which needs
 * to see the existing list, and a toggle that reads as one action to the
 * caller.
 *
 * Everything goes through the backend contract, so the local backend serves
 * the test suite and any offline run without this file knowing.
 */

import {
  FAVOURITE_KINDS,
  FAVOURITE_LIMITS,
  buildFavourite,
  favouriteId,
  groupFavourites,
} from "../../data/favourites";
import { getBackend } from "../backend";

export { FAVOURITE_KINDS, FAVOURITE_LIMITS };

/** Everything this person has starred, split by kind. */
export async function listFavourites(uid) {
  if (!uid) return { teams: [], players: [] };
  const backend = getBackend();
  if (!backend?.listFavourites) return { teams: [], players: [] };

  return groupFavourites(await backend.listFavourites(uid));
}

/**
 * Thrown when someone is at the cap for a kind.
 *
 * A named error rather than a silent no-op: the caller has to tell them
 * why the star did not stick, and "nothing happened" is the worst possible
 * answer to a tap.
 */
export class FavouriteLimitReached extends Error {
  constructor(kind, limit) {
    super(
      kind === FAVOURITE_KINDS.TEAM
        ? `You can follow up to ${limit} clubs. Unfollow one to add another.`
        : `You can follow up to ${limit} players. Unfollow one to add another.`
    );
    this.name = "FavouriteLimitReached";
    this.kind = kind;
    this.limit = limit;
  }
}

/**
 * Star something.
 *
 * Idempotent: starring what is already starred rewrites the same document
 * rather than failing, because a double tap on a slow connection should
 * not be an error.
 */
export async function addFavourite(uid, kind, refId, now = Date.now()) {
  if (!uid) throw new Error("Signing in is what makes a favourite yours.");

  const backend = getBackend();
  const favourite = buildFavourite({ kind, refId, addedAt: now });

  const existing = await backend.listFavourites(uid);
  const alreadyStarred = existing.some(
    (record) => record.favouriteId === favourite.favouriteId
  );

  if (!alreadyStarred) {
    const limit = FAVOURITE_LIMITS[kind];
    const held = existing.filter((record) => record.kind === kind).length;
    if (held >= limit) throw new FavouriteLimitReached(kind, limit);
  }

  await backend.saveFavourite(uid, favourite);
  return favourite;
}

export async function removeFavourite(uid, kind, refId) {
  if (!uid) return;
  await getBackend().removeFavourite(uid, favouriteId(kind, refId));
}

/** Star or unstar, returning whether it is starred afterwards. */
export async function toggleFavourite(uid, kind, refId, isStarred, now) {
  if (isStarred) {
    await removeFavourite(uid, kind, refId);
    return false;
  }
  await addFavourite(uid, kind, refId, now);
  return true;
}
